import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';
import { logger } from '../lib/logger';

const AgencyContext = createContext(null);

export const useAgency = () => {
  const context = useContext(AgencyContext);
  if (!context) {
    // Return default values if context not available
    return {
      agencies: [],
      currentAgency: null,
      loading: false,
      isAzaiStaff: false,
      fetchAgencies: () => {},
      switchAgency: () => {},
      createAgency: () => {},
      updateAgency: () => {},
      updateContract: () => {},
      canAccessAllAgencies: false,
      azaiRole: null,
    };
  }
  return context;
};

export const AgencyProvider = ({ children }) => {
  const { user } = useAuth();
  const [agencies, setAgencies] = useState([]);
  const [currentAgency, setCurrentAgency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAzaiStaff, setIsAzaiStaff] = useState(false);

  const fetchAgencies = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/agencies/');
      setAgencies(response.data.agencies || []);

      // Determine if user is AZAI staff
      const entityType = user?.entity_type;
      setIsAzaiStaff(entityType === 'azai');

      // Set current agency — no demo/first-agency fallback (Phase 4):
      // AZAI staff must explicitly select + elevate; agency users default
      // only when they have exactly one membership.
      if (response.data.agencies?.length > 0) {
        const savedAgencyId = localStorage.getItem('currentAgencyId') || user?.current_agency_id;
        const savedAgency = response.data.agencies.find(a => a.id === savedAgencyId);
        if (savedAgency) {
          setCurrentAgency(savedAgency);
        } else if (entityType !== 'azai' && response.data.agencies.length === 1) {
          setCurrentAgency(response.data.agencies[0]);
          localStorage.setItem('currentAgencyId', response.data.agencies[0].id);
        } else {
          setCurrentAgency(null);
        }
      }
    } catch (error) {
      logger.error('Failed to fetch agencies:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch agencies on mount (cookie-based auth — no token check needed).
  // `fetchAgencies` is declared above with `useCallback([user])` so this
  // effect re-runs whenever the user changes; satisfies
  // `react-hooks/exhaustive-deps` without re-fetching on unrelated renders.
  useEffect(() => {
    if (user) {
      fetchAgencies();
    }
  }, [user, fetchAgencies]);

  const switchAgency = useCallback((agency) => {
    setCurrentAgency(agency);
    localStorage.setItem('currentAgencyId', agency.id);
  }, []);

  const createAgency = useCallback(async (agencyData) => {
    const response = await api.post('/agencies/', agencyData);
    await fetchAgencies();
    return response.data;
  }, [fetchAgencies]);

  const updateAgency = useCallback(async (agencyId, agencyData) => {
    const response = await api.put(`/agencies/${agencyId}`, agencyData);
    await fetchAgencies();
    return response.data;
  }, [fetchAgencies]);

  const updateContract = useCallback(async (agencyId, contractType, params) => {
    const queryParams = new URLSearchParams(params).toString();
    const response = await api.put(`/agencies/${agencyId}/contracts/${contractType}?${queryParams}`);
    await fetchAgencies();
    return response.data;
  }, [fetchAgencies]);

  const value = {
    // State
    agencies,
    currentAgency,
    loading,
    isAzaiStaff,
    
    // Actions
    fetchAgencies,
    switchAgency,
    createAgency,
    updateAgency,
    updateContract,
    
    // Helpers
    canAccessAllAgencies: isAzaiStaff && user?.assigned_agencies?.includes('*'),
    azaiRole: user?.azai_role,
  };

  return (
    <AgencyContext.Provider value={value}>
      {children}
    </AgencyContext.Provider>
  );
};

export default AgencyContext;
